/**
 * fiko-cam3d-motoru.ts — GERÇEK 3D CAM motoru (CATIA Surface Machining benzeri).
 *
 * Mesh tabanlı (govdeMesh / import STEP mesh), three-mesh-bvh ile hızlandırılmış ray-cast.
 * Operasyonlar:
 *   • Z-Level (Cavity) Roughing — katman-katman raster kaba boşaltma + stock-to-leave
 *   • Parallel / Sweeping Finishing — XY grid ray-cast + GOUGE-FREE ball-end drop + scallop step-over
 *   • Waterline / ZLevel Finishing — mesh∩düzlem kontur takibi (dik duvar)
 *   • Pencil Tracing — iç konkav köşe/oluk takibi
 * Çıktı: dahili toolpath modeli → görselleştirme (renkli fat-line) + ISO G-code (pluggable post).
 * Performans: BVH, ön-allocate edilen vektörler, zero-alloc sıcak döngü, ray havuzu.
 */
import * as THREE from 'three';
import { MeshBVH } from 'three-mesh-bvh';

export type TakimTip = 'kure' | 'duz' | 'bullnose';
export interface ITakim { tip: TakimTip; cap: number; kose?: number; }            // cap=çap mm, kose=bullnose köşe R
export interface IFeeds { devir: number; ilerleme: number; dalma: number; rapid: number; }   // rpm, mm/dk

export interface ICamParam {
  takim: ITakim;
  feeds: IFeeds;
  guvenliZ: number;        // rapid clearance (mm, mutlak)
  stepover?: number;       // yanal adım (yoksa scallop'tan)
  scallop?: number;        // izin verilen kalıntı yüksekliği (mm) → stepover hesaplar
  stepdown?: number;       // eksenel katman derinliği (roughing/waterline)
  stockLeave?: number;     // finiş payı (mm)
  eksen?: 'x' | 'y';       // tarama yönü (parallel)
  zigzag?: boolean;        // tek yön / zik-zak
  // ── Giriş/Çıkış (lead-in/out) + yaklaşma/uzaklaşma — CATIA tarzı, tüm hareketler ayarlanabilir ──
  yaklasmaZ?: number;      // yüzey üstü yaklaşma payı (rapid bu yüksekliğe iner, sonra giriş hareketi) — vars. 2
  girisTip?: 'dik' | 'rampa' | 'yay';   // lead-in: dik plonj / rampa / teğet yay
  cikisTip?: 'dik' | 'rampa' | 'yay';   // lead-out
  girisBoy?: number;       // rampa yatay boyu / yay yarıçapı (mm) — vars. takım çapı
}

export type HareketTip = 'rapid' | 'kesim' | 'dalma' | 'cikis';
export interface IHareket { tip: HareketTip; noktalar: THREE.Vector3[]; }
export interface IToolpath {
  hareketler: IHareket[];
  meta: { operasyon: string; takim: ITakim; feeds: IFeeds; toplamMesafe: number; kesimMesafe: number; sureDk: number; nokta: number; };
}

const _ray = new THREE.Ray();
const _down = new THREE.Vector3(0, 0, -1);
const _hit = new THREE.Vector3();

export class FikoCam3DMotoru {
  private bvh: MeshBVH | null = null;
  private geo: THREE.BufferGeometry | null = null;
  private bbox = new THREE.Box3();
  private mat = new THREE.Matrix4();   // mesh world matrix (ray dünya→yerel)
  private matTers = new THREE.Matrix4();

  /** Mesh'ten BVH kur (bir kez; tekrar tekrar raycast hızlı). */
  kur(mesh: THREE.Mesh): boolean {
    const geo = mesh.geometry as THREE.BufferGeometry; if (!geo?.attributes['position']) return false;
    this.geo = geo;
    this.bvh = new MeshBVH(geo);
    (geo as any).boundsTree = this.bvh;
    mesh.updateMatrixWorld(true);
    this.mat.copy(mesh.matrixWorld); this.matTers.copy(this.mat).invert();
    geo.computeBoundingBox();
    this.bbox.copy(geo.boundingBox!).applyMatrix4(this.mat);
    return true;
  }

  /** (x,y) noktasından AŞAĞI ray-cast → yüzey yüksekliği (dünya Z) veya null (boşluk). */
  private yuzeyZ(x: number, y: number, zUst: number): number | null {
    if (!this.bvh) return null;
    // dünya ray'i → mesh yerel uzayına çevir (BVH yerel)
    _ray.origin.set(x, y, zUst); _ray.direction.copy(_down);
    _ray.applyMatrix4(this.matTers);
    const hit = this.bvh.raycastFirst(_ray, THREE.DoubleSide);
    if (!hit) return null;
    _hit.copy(hit.point).applyMatrix4(this.mat);   // yerel→dünya
    return _hit.z;
  }

  /** GOUGE-FREE ball-end / flat / bullnose drop: takım merkezinin (x,y)'de inebileceği EN DÜŞÜK tip Z. */
  private takimDrop(x: number, y: number, zUst: number, t: ITakim): number | null {
    const r = t.cap / 2;
    const merkezZ0 = this.yuzeyZ(x, y, zUst);
    if (merkezZ0 == null) return null;          // tam altta yüzey yok
    if (t.tip === 'duz') {
      // düz: footprint disk içindeki EN YÜKSEK yüzey (tabanı oraya oturur)
      let enUst = merkezZ0;
      for (const [dx, dy] of this.diskOrnek(r)) { const z = this.yuzeyZ(x + dx, y + dy, zUst); if (z != null && z > enUst) enUst = z; }
      return enUst;   // tip = taban
    }
    // küre / bullnose: ball merkezi için footprint örnekle (gouge önle)
    const rb = t.tip === 'bullnose' ? Math.max(0.1, t.kose || r) : r;   // efektif yuvarlama
    let merkezZ = merkezZ0 + rb;                // dik altta katkı = +rb
    for (const [dx, dy] of this.diskOrnek(rb)) {
      const z = this.yuzeyZ(x + dx, y + dy, zUst); if (z == null) continue;
      const d2 = dx * dx + dy * dy; if (d2 >= rb * rb) continue;
      const gerek = z + Math.sqrt(rb * rb - d2);  // bu yüzey noktasına değmeden merkezin olması gereken min Z
      if (gerek > merkezZ) merkezZ = gerek;
    }
    return merkezZ - rb;   // tip (ucun en alt noktası)
  }

  /** Takım footprint disk örnekleme noktaları (3 halka, gouge testi için). */
  private _diskCache = new Map<number, [number, number][]>();
  private diskOrnek(r: number): [number, number][] {
    const key = Math.round(r * 10);
    let pts = this._diskCache.get(key);
    if (pts) return pts;
    pts = [];
    const halka = [0.4, 0.75, 0.98];
    for (const f of halka) { const rr = r * f, n = Math.max(6, Math.round(f * 12)); for (let i = 0; i < n; i++) { const a = (i / n) * Math.PI * 2; pts.push([Math.cos(a) * rr, Math.sin(a) * rr]); } }
    this._diskCache.set(key, pts);
    return pts;
  }

  /** Scallop yüksekliğinden step-over (ball-end): 2√(2Rh−h²). */
  private adimHesap(p: ICamParam): number {
    if (p.stepover && p.stepover > 0) return p.stepover;
    const R = p.takim.cap / 2, h = p.scallop || 0.05;
    return Math.max(0.05, 2 * Math.sqrt(Math.max(1e-6, 2 * R * h - h * h)));
  }

  // ───────────────────── PARALLEL / SWEEPING FINISHING ─────────────────────
  parallelFinish(p: ICamParam): IToolpath {
    const hareketler: IHareket[] = [];
    const adim = this.adimHesap(p);
    const ilerStep = Math.max(0.3, adim * 0.5);   // tarama ekseni boyunca örnekleme sıklığı
    const zUst = this.bbox.max.z + 5;
    const xEksen = (p.eksen || 'x') === 'x';
    const aMin = xEksen ? this.bbox.min.x : this.bbox.min.y, aMax = xEksen ? this.bbox.max.x : this.bbox.max.y;
    const bMin = xEksen ? this.bbox.min.y : this.bbox.min.x, bMax = xEksen ? this.bbox.max.y : this.bbox.max.x;
    let yon = 1;
    for (let b = bMin; b <= bMax + 1e-6; b += adim) {
      const pas: THREE.Vector3[] = [];
      const a0 = yon > 0 ? aMin : aMax, a1 = yon > 0 ? aMax : aMin, st = yon > 0 ? ilerStep : -ilerStep;
      for (let a = a0; yon > 0 ? a <= a1 + 1e-6 : a >= a1 - 1e-6; a += st) {
        const x = xEksen ? a : b, y = xEksen ? b : a;
        const z = this.takimDrop(x, y, zUst, p.takim);
        if (z != null) pas.push(new THREE.Vector3(x, y, z + (p.stockLeave || 0)));
        else if (pas.length) { this.pasEkle(hareketler, pas.splice(0), p); }   // boşluk → pası kes
      }
      if (pas.length) this.pasEkle(hareketler, pas, p);
      if (p.zigzag !== false) yon = -yon;
    }
    return this.toolpathTamamla(hareketler, 'Parallel Finishing', p);
  }

  /** Bir kesim pasını rapid-yaklaşma + dalma + kesim olarak ekler. */
  private pasEkle(hareketler: IHareket[], pas: THREE.Vector3[], p: ICamParam): void {
    if (pas.length < 2) return;
    const bas = pas[0], son = pas[pas.length - 1];
    const yak = p.yaklasmaZ ?? 2, R = p.girisBoy || p.takim.cap;
    // ilk/son kesim yönü (yatay)
    const dirBas = new THREE.Vector3(pas[1].x - bas.x, pas[1].y - bas.y, 0); if (dirBas.lengthSq() < 1e-9) dirBas.set(1, 0, 0); dirBas.normalize();
    const dirSon = new THREE.Vector3(son.x - pas[pas.length - 2].x, son.y - pas[pas.length - 2].y, 0); if (dirSon.lengthSq() < 1e-9) dirSon.set(1, 0, 0); dirSon.normalize();
    // 1) Rapid güvenli Z + giriş başlangıcının üstüne
    const giris = this.leadNoktalari(bas, dirBas, p.girisTip || 'dik', R, yak, true);
    const ilk = giris[0];
    hareketler.push({ tip: 'rapid', noktalar: [new THREE.Vector3(ilk.x, ilk.y, p.guvenliZ)] });
    hareketler.push({ tip: 'rapid', noktalar: [new THREE.Vector3(ilk.x, ilk.y, p.guvenliZ), ilk.clone()] });
    // 2) Giriş (lead-in) → kesime bağlan
    hareketler.push({ tip: 'dalma', noktalar: giris });
    // 3) Kesim
    hareketler.push({ tip: 'kesim', noktalar: pas });
    // 4) Çıkış (lead-out)
    const cikis = this.leadNoktalari(son, dirSon, p.cikisTip || 'dik', R, yak, false);
    hareketler.push({ tip: 'cikis', noktalar: cikis });
    // 5) Rapid güvenli Z'ye
    const cson = cikis[cikis.length - 1];
    hareketler.push({ tip: 'rapid', noktalar: [cson.clone(), new THREE.Vector3(cson.x, cson.y, p.guvenliZ)] });
  }

  /** Lead-in/out nokta dizisi. giris=true: yaklaşmadan kesime; false: kesimden uzaklaşmaya.
   * dik=plonj, rampa=eğimli doğru, yay=teğet çeyrek yay. nokta(kesim ucu) dahil. */
  private leadNoktalari(uc: THREE.Vector3, dir: THREE.Vector3, tip: 'dik' | 'rampa' | 'yay', R: number, yak: number, giris: boolean): THREE.Vector3[] {
    const d = dir.clone();   // kesim yönü (uçtan ileri)
    if (tip === 'yay') {
      const merkez = new THREE.Vector3(uc.x, uc.y, uc.z + R);
      const pts: THREE.Vector3[] = [];
      const N = 8;
      for (let i = 0; i <= N; i++) {
        const t = i / N, ang = (Math.PI / 2) * (giris ? (1 - t) : t);   // giriş: 90°→0°, çıkış: 0°→90°
        // P = merkez + (-d)*R sin + (0,0,-1)*R cos  → uçta tanjant ±d
        const yon = giris ? -1 : 1;   // giriş arkadan, çıkış ileri
        pts.push(new THREE.Vector3(merkez.x + yon * d.x * R * Math.sin(ang), merkez.y + yon * d.y * R * Math.sin(ang), merkez.z - R * Math.cos(ang)));
      }
      return pts;   // giriş: ilk=üst/arka, son=uç; çıkış: ilk=uç, son=üst/ileri
    }
    if (tip === 'rampa') {
      const off = (giris ? -1 : 1);
      const uzak = new THREE.Vector3(uc.x + off * d.x * R, uc.y + off * d.y * R, uc.z + yak);
      return giris ? [uzak, uc.clone()] : [uc.clone(), uzak];
    }
    // dik (vertical plunge/retract)
    const ust = new THREE.Vector3(uc.x, uc.y, uc.z + yak);
    return giris ? [ust, uc.clone()] : [uc.clone(), ust];
  }

  // ───────────────────── Z-LEVEL (CAVITY) ROUGHING ─────────────────────
  zLevelRough(p: ICamParam): IToolpath {
    const hareketler: IHareket[] = [];
    const adim = this.adimHesap(p) * 1.4;   // kabada daha geniş yanal
    const ilerStep = Math.max(0.5, adim * 0.6);
    const sd = Math.max(0.5, p.stepdown || p.takim.cap * 0.5);
    const leave = p.stockLeave ?? 0.3;
    const zUst = this.bbox.max.z, zAlt = this.bbox.min.z + leave;
    const r = p.takim.cap / 2;
    for (let z = zUst - sd; z >= zAlt - 1e-6; z -= sd) {
      const seviyeZ = Math.max(zAlt, z);
      let yon = 1;
      for (let y = this.bbox.min.y; y <= this.bbox.max.y + 1e-6; y += adim) {
        const pas: THREE.Vector3[] = [];
        const x0 = yon > 0 ? this.bbox.min.x : this.bbox.max.x, x1 = yon > 0 ? this.bbox.max.x : this.bbox.min.x, st = yon > 0 ? ilerStep : -ilerStep;
        for (let x = x0; yon > 0 ? x <= x1 + 1e-6 : x >= x1 - 1e-6; x += st) {
          // bu seviyede malzeme var mı: parça yüzeyi bu Z'nin ALTINDA mı (üstü stok)
          const ys = this.takimDrop(x, y, this.bbox.max.z + 5, p.takim);
          const parcaUst = ys != null ? ys + leave : this.bbox.min.z;
          if (seviyeZ > parcaUst + 1e-3) pas.push(new THREE.Vector3(x, y, seviyeZ));   // bu noktada stok kaldır
          else if (pas.length) this.pasEkle(hareketler, pas.splice(0), p);
        }
        if (pas.length) this.pasEkle(hareketler, pas, p);
        yon = -yon;
      }
    }
    return this.toolpathTamamla(hareketler, 'Z-Level Roughing', p);
  }

  // ───────────────────── WATERLINE / ZLEVEL FINISHING ─────────────────────
  waterline(p: ICamParam): IToolpath {
    const hareketler: IHareket[] = [];
    const sd = Math.max(0.2, p.stepdown || (p.takim.cap * 0.15));
    const r = p.takim.cap / 2 + (p.stockLeave || 0);   // TAKIM YARIÇAPI offset (+ finiş payı)
    for (let z = this.bbox.max.z - sd; z >= this.bbox.min.z + 1e-6; z -= sd) {
      const konturlar = this.meshKontur(z);
      for (const ham of konturlar) {
        if (ham.length < 3) continue;
        const k = this.konturOffset(ham, r);   // takım MERKEZİ konturu = parça konturu + r (gömülmez)
        if (k.length < 3) continue;
        const bas = k[0];
        hareketler.push({ tip: 'rapid', noktalar: [new THREE.Vector3(bas.x, bas.y, p.guvenliZ)] });
        hareketler.push({ tip: 'dalma', noktalar: [new THREE.Vector3(bas.x, bas.y, p.guvenliZ), bas.clone()] });
        hareketler.push({ tip: 'kesim', noktalar: [...k, bas.clone()] });
        hareketler.push({ tip: 'rapid', noktalar: [bas.clone(), new THREE.Vector3(bas.x, bas.y, p.guvenliZ)] });
      }
    }
    return this.toolpathTamamla(hareketler, 'Waterline Finishing', p);
  }

  /** Kapalı XY konturu, takım yarıçapı kadar DIŞA ötele (her vertex 2D normal yönünde).
   * İşaret signed-alandan: dış kontur (CCW, alan>0) → dışa(+); delik (CW, alan<0) → içe.
   * Böylece ball/flat takım MERKEZİ parça yüzeyine r uzaklıkta kalır (gömülmez). */
  private konturOffset(k: THREE.Vector3[], r: number): THREE.Vector3[] {
    const n = k.length; if (n < 3 || r <= 0) return k;
    let alan = 0; for (let i = 0; i < n; i++) { const a = k[i], b = k[(i + 1) % n]; alan += a.x * b.y - b.x * a.y; }
    // sol-normal CCW'de İÇE bakar → dışa offset için -isaret. CCW(alan>0) dış kontur → dışa(+), CW delik → içe.
    const isaret = alan >= 0 ? -1 : 1;
    const out: THREE.Vector3[] = [];
    for (let i = 0; i < n; i++) {
      const onceki = k[(i - 1 + n) % n], cur = k[i], sonraki = k[(i + 1) % n];
      const e1x = cur.x - onceki.x, e1y = cur.y - onceki.y, e2x = sonraki.x - cur.x, e2y = sonraki.y - cur.y;
      let nx = (-e1y - e2y), ny = (e1x + e2x); const L = Math.hypot(nx, ny) || 1; nx /= L; ny /= L;
      out.push(new THREE.Vector3(cur.x + nx * r * isaret, cur.y + ny * r * isaret, cur.z));
    }
    return out;
  }

  /** Mesh ∩ yatay düzlem (Z) → kapalı kontur(lar) (üçgen-düzlem kesişimi + uç-uca zincir). */
  private meshKontur(z: number): THREE.Vector3[][] {
    const geo = this.geo; if (!geo) return [];
    const pos = geo.attributes['position'] as THREE.BufferAttribute; const idx = geo.index;
    const segler: [THREE.Vector3, THREE.Vector3][] = [];
    const va = new THREE.Vector3(), vb = new THREE.Vector3(), vc = new THREE.Vector3();
    const triSay = idx ? idx.count / 3 : pos.count / 3;
    const kesim = (p1: THREE.Vector3, p2: THREE.Vector3): THREE.Vector3 | null => {
      const d1 = p1.z - z, d2 = p2.z - z; if ((d1 > 0) === (d2 > 0)) return null; const t = d1 / (d1 - d2);
      return new THREE.Vector3(p1.x + (p2.x - p1.x) * t, p1.y + (p2.y - p1.y) * t, z);
    };
    for (let i = 0; i < triSay; i++) {
      const i0 = idx ? idx.getX(i * 3) : i * 3, i1 = idx ? idx.getX(i * 3 + 1) : i * 3 + 1, i2 = idx ? idx.getX(i * 3 + 2) : i * 3 + 2;
      va.fromBufferAttribute(pos, i0).applyMatrix4(this.mat); vb.fromBufferAttribute(pos, i1).applyMatrix4(this.mat); vc.fromBufferAttribute(pos, i2).applyMatrix4(this.mat);
      const zMin = Math.min(va.z, vb.z, vc.z), zMax = Math.max(va.z, vb.z, vc.z); if (z < zMin || z > zMax) continue;
      const pts: THREE.Vector3[] = []; const e1 = kesim(va, vb), e2 = kesim(vb, vc), e3 = kesim(vc, va);
      if (e1) pts.push(e1); if (e2) pts.push(e2); if (e3) pts.push(e3);
      if (pts.length === 2) segler.push([pts[0], pts[1]]);
    }
    return this.segZincirle(segler);
  }

  /** Segmentleri uç-uca bağlayıp kapalı kontur dizilerine çevirir (Q=0.05 tol). */
  private segZincirle(segler: [THREE.Vector3, THREE.Vector3][]): THREE.Vector3[][] {
    const Q = 0.05, key = (v: THREE.Vector3) => `${Math.round(v.x / Q)},${Math.round(v.y / Q)}`;
    const harita = new Map<string, [THREE.Vector3, THREE.Vector3][]>();
    for (const s of segler) { for (const v of s) { const k = key(v); if (!harita.has(k)) harita.set(k, []); harita.get(k)!.push(s); } }
    const kullanildi = new Set<[THREE.Vector3, THREE.Vector3]>(); const out: THREE.Vector3[][] = [];
    for (const s0 of segler) {
      if (kullanildi.has(s0)) continue;
      const kontur: THREE.Vector3[] = [s0[0], s0[1]]; kullanildi.add(s0);
      let devam = true, guard = 0;
      while (devam && guard++ < 100000) {
        devam = false; const uc = kontur[kontur.length - 1];
        for (const s of (harita.get(key(uc)) || [])) {
          if (kullanildi.has(s)) continue;
          const yeni = key(s[0]) === key(uc) ? s[1] : s[0];
          kontur.push(yeni); kullanildi.add(s); devam = true; break;
        }
      }
      if (kontur.length >= 3) out.push(kontur);
    }
    return out;
  }

  // ───────────────────── PENCIL TRACING (iç konkav köşe) ─────────────────────
  pencil(p: ICamParam): IToolpath {
    const hareketler: IHareket[] = []; const geo = this.geo; if (!geo) return this.toolpathTamamla(hareketler, 'Pencil', p);
    const eg = this.kenarCikarKonkav(35);   // konkav (iç) kenarlar
    for (const k of eg) {
      if (k.length < 2) continue;
      const bas = k[0];
      hareketler.push({ tip: 'rapid', noktalar: [new THREE.Vector3(bas.x, bas.y, p.guvenliZ)] });
      hareketler.push({ tip: 'dalma', noktalar: [new THREE.Vector3(bas.x, bas.y, p.guvenliZ), bas.clone()] });
      hareketler.push({ tip: 'kesim', noktalar: k });
      const son = k[k.length - 1];
      hareketler.push({ tip: 'rapid', noktalar: [son.clone(), new THREE.Vector3(son.x, son.y, p.guvenliZ)] });
    }
    return this.toolpathTamamla(hareketler, 'Pencil Tracing', p);
  }

  /** Konkav (iç bükey) kenarları çıkar: komşu üçgen normalleri içe katlanan paylaşılan kenarlar. */
  private kenarCikarKonkav(esikDeg: number): THREE.Vector3[][] {
    const geo = this.geo!; const pos = geo.attributes['position'] as THREE.BufferAttribute; const idx = geo.index; if (!idx) return [];
    const cos = Math.cos(esikDeg * Math.PI / 180);
    const triN: THREE.Vector3[] = []; const triMerkez: THREE.Vector3[] = [];
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    const n = idx.count / 3;
    for (let i = 0; i < n; i++) { a.fromBufferAttribute(pos, idx.getX(i * 3)); b.fromBufferAttribute(pos, idx.getX(i * 3 + 1)); c.fromBufferAttribute(pos, idx.getX(i * 3 + 2)); const nor = new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(c, a)).normalize(); triN.push(nor); triMerkez.push(new THREE.Vector3((a.x + b.x + c.x) / 3, (a.y + b.y + c.y) / 3, (a.z + b.z + c.z) / 3)); }
    const Q = 1e-3, kenarMap = new Map<string, { t: number; v0: number; v1: number }[]>();
    const vk = (i: number) => { const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i); return `${Math.round(x / Q)},${Math.round(y / Q)},${Math.round(z / Q)}`; };
    const ekle = (v0: number, v1: number, t: number) => { const k = [vk(v0), vk(v1)].sort().join('|'); if (!kenarMap.has(k)) kenarMap.set(k, []); kenarMap.get(k)!.push({ t, v0, v1 }); };
    for (let i = 0; i < n; i++) { const i0 = idx.getX(i * 3), i1 = idx.getX(i * 3 + 1), i2 = idx.getX(i * 3 + 2); ekle(i0, i1, i); ekle(i1, i2, i); ekle(i2, i0, i); }
    const out: THREE.Vector3[][] = [];
    for (const arr of kenarMap.values()) {
      if (arr.length !== 2) continue; const [e1, e2] = arr;
      const d = triN[e1.t].dot(triN[e2.t]); if (d > cos) continue;   // düz/teğet → atla
      // konkavlık: kenar orta noktasından komşu merkeze yön normal'e karşı mı
      const p0 = new THREE.Vector3().fromBufferAttribute(pos, e1.v0).applyMatrix4(this.mat);
      const p1 = new THREE.Vector3().fromBufferAttribute(pos, e1.v1).applyMatrix4(this.mat);
      const orta = p0.clone().add(p1).multiplyScalar(0.5);
      const dirKom = triMerkez[e2.t].clone().applyMatrix4(this.mat).sub(orta);
      if (dirKom.dot(triN[e1.t].clone().transformDirection(this.mat)) > 0) out.push([p0, p1]);   // konkav
    }
    return out;
  }

  // ───────────────────── TOOLPATH TAMAMLA + İSTATİSTİK ─────────────────────
  private toolpathTamamla(hareketler: IHareket[], op: string, p: ICamParam): IToolpath {
    let toplam = 0, kesim = 0, nokta = 0, sure = 0;
    for (const h of hareketler) {
      for (let i = 1; i < h.noktalar.length; i++) { const d = h.noktalar[i].distanceTo(h.noktalar[i - 1]); toplam += d; nokta++; const f = h.tip === 'rapid' ? p.feeds.rapid : h.tip === 'dalma' ? p.feeds.dalma : p.feeds.ilerleme; if (h.tip !== 'rapid') kesim += d; sure += d / Math.max(1, f); }
    }
    return { hareketler, meta: { operasyon: op, takim: p.takim, feeds: p.feeds, toplamMesafe: +toplam.toFixed(1), kesimMesafe: +kesim.toFixed(1), sureDk: +sure.toFixed(2), nokta } };
  }

  // ───────────────────── GÖRSELLEŞTİRME (renkli fat-line) ─────────────────────
  gorsellestir(tp: IToolpath): THREE.Group {
    const grup = new THREE.Group(); grup.name = 'FikoCam3DYol';
    const renk: Record<HareketTip, number> = { rapid: 0x33dd55, kesim: 0x22d3ee, dalma: 0xffaa00, cikis: 0xff5577 };
    const buk: Record<HareketTip, number[]> = { rapid: [], kesim: [], dalma: [], cikis: [] };
    for (const h of tp.hareketler) { const arr = buk[h.tip]; for (let i = 1; i < h.noktalar.length; i++) { const a = h.noktalar[i - 1], b = h.noktalar[i]; arr.push(a.x, a.y, a.z, b.x, b.y, b.z); } }
    for (const tip of ['kesim', 'dalma', 'cikis', 'rapid'] as HareketTip[]) {
      if (!buk[tip].length) continue;
      const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(buk[tip], 3));
      const m = new THREE.LineBasicMaterial({ color: renk[tip], transparent: true, opacity: tip === 'rapid' ? 0.35 : 0.95, depthTest: true });
      const ls = new THREE.LineSegments(g, m); ls.name = 'cam-' + tip; (ls as any).raycast = () => { };
      grup.add(ls);
    }
    return grup;
  }

  // ───────────────────── ISO G-CODE (pluggable post) ─────────────────────
  gcode(tp: IToolpath, p: ICamParam, isim = 'FIKOYT'): string {
    const L: string[] = [];
    const f = p.feeds; const yu = (n: number) => n.toFixed(3);
    L.push('%'); L.push('(' + isim + ' - ' + tp.meta.operasyon + ')');
    L.push('(Takim: ' + p.takim.tip + ' D' + p.takim.cap + 'mm)');
    L.push('(Sure ~' + tp.meta.sureDk + ' dk, Mesafe ' + tp.meta.toplamMesafe + ' mm)');
    L.push('G21 G90 G54'); L.push('G0 Z' + yu(p.guvenliZ));
    L.push('M3 S' + Math.round(f.devir));
    let sonF = -1;
    for (const h of tp.hareketler) {
      for (let i = 0; i < h.noktalar.length; i++) {
        const pt = h.noktalar[i];
        if (h.tip === 'rapid') { L.push('G0 X' + yu(pt.x) + ' Y' + yu(pt.y) + ' Z' + yu(pt.z)); sonF = -1; }
        else { const fr = h.tip === 'dalma' ? f.dalma : f.ilerleme; const fStr = fr !== sonF ? ' F' + Math.round(fr) : ''; sonF = fr; L.push('G1 X' + yu(pt.x) + ' Y' + yu(pt.y) + ' Z' + yu(pt.z) + fStr); }
      }
    }
    L.push('G0 Z' + yu(p.guvenliZ)); L.push('M5'); L.push('M30'); L.push('%');
    return L.join('\n');
  }

  /** IToolpath → FikoSimilasyonV1 nokta formatı (THREE.Vector3 + hiz + hareketTuru + takim + takimYonu). */
  simNoktalari(tp: IToolpath, feeds: IFeeds, takim: ITakim): any[] {
    const out: any[] = [];
    const tur: Record<HareketTip, string> = { rapid: 'YAKLASMA', kesim: 'ISLEME', dalma: 'YAKLASMA', cikis: 'UZAKLASMA' };
    const hiz: Record<HareketTip, number> = { rapid: feeds.rapid, kesim: feeds.ilerleme, dalma: feeds.dalma, cikis: feeds.ilerleme };
    // Paylaşılan takım mesh'i (ucu origin'de, +Z boyunca) — GERÇEK TAKIM ŞEKLİ (ball/flat/bullnose)
    // ki düz-silindir kenarı eğri yüzeye girmiş gibi GÖRÜNMESİN (yol ball-end için hesaplı).
    const r = takim.cap / 2, boy = Math.max(24, takim.cap * 4);
    const mat = new THREE.MeshStandardMaterial({ color: 0xb0b8c0, metalness: 0.85, roughness: 0.25 });
    const toolMesh: any = new THREE.Group();
    if (takim.tip === 'kure') {
      const kure = new THREE.Mesh(new THREE.SphereGeometry(r, 24, 16), mat); kure.position.z = r;   // alt yarım küre ucu z=0'da
      const sap = new THREE.Mesh(new THREE.CylinderGeometry(r, r, boy - r, 20), mat); sap.rotateX(Math.PI / 2); sap.position.z = r + (boy - r) / 2;
      toolMesh.add(kure, sap);
    } else if (takim.tip === 'bullnose') {
      const kose = Math.min(r, takim.kose || r * 0.3);
      const torus = new THREE.Mesh(new THREE.TorusGeometry(Math.max(0.01, r - kose), kose, 12, 24), mat); torus.position.z = kose;
      const sap = new THREE.Mesh(new THREE.CylinderGeometry(r, r, boy - kose, 20), mat); sap.rotateX(Math.PI / 2); sap.position.z = kose + (boy - kose) / 2;
      toolMesh.add(torus, sap);
    } else {
      const sap = new THREE.Mesh(new THREE.CylinderGeometry(r, r, boy, 20), mat); sap.rotateX(Math.PI / 2); sap.position.z = boy / 2;
      toolMesh.add(sap);
    }
    toolMesh.name = 'cam3dTakim'; toolMesh.cap = takim.cap; toolMesh.takimCap = takim.cap; toolMesh.takimUzunluk = boy;
    // TAKIM GEOMETRİSİ tipi (sim removal + Dexel/SDF/ZBuffer engine'leri bunu okuyup ball/bull/flat işler)
    toolMesh.takimTipi = takim.tip === 'kure' ? 'BallNose' : takim.tip === 'bullnose' ? 'BullNose' : 'Flat';
    toolMesh.radius = takim.tip === 'kure' ? r : takim.tip === 'bullnose' ? Math.min(r, takim.kose || r * 0.3) : 0;
    const yon = new THREE.Vector3(0, 0, 1);   // 3-eksen dik takım
    for (const h of tp.hareketler) {
      for (const p of h.noktalar) {
        const v: any = new THREE.Vector3(p.x, p.y, p.z);
        v.hiz = hiz[h.tip]; v.hareketTuru = tur[h.tip]; v.guncelSure = 0; v.devir = feeds.devir;
        v.takimYonu = yon; v.takim = toolMesh; v.takimCap = takim.cap; v.takimUzunluk = boy;
        out.push(v);
      }
    }
    return out;
  }

  dispose(): void { this.bvh = null; this.geo = null; this._diskCache.clear(); }
}

export const fikoCam3D = new FikoCam3DMotoru();
